import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from './AuthContext';

/**
 * Global cache of `patient_id → has_signed_rgpd`. Loaded once at login from
 * /api/patients/rgpd-signed-ids so every screen that renders a patient name
 * can show the discreet shield badge without a per-row fetch.
 *
 * Refresh API:
 *   const { isSigned, refresh } = useRgpd();
 *   isSigned(patientId)              → boolean
 *   refresh()                        → re-fetches from backend (call after
 *                                       a new consent is signed so the UI
 *                                       updates without a full reload)
 */
const RgpdContext = createContext({
  isSigned: () => false,
  refresh: async () => {},
});

export function RgpdProvider({ children }) {
  const { admin } = useAuth();
  const [signedIds, setSignedIds] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!admin) {
      setSignedIds(new Set());
      return;
    }
    try {
      const res = await api.get('/patients/rgpd-signed-ids');
      setSignedIds(new Set(res.data?.patient_ids || []));
    } catch {
      // Silent — badge just doesn't appear; not worth bothering the user.
    }
  }, [admin]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = {
    isSigned: (id) => !!id && signedIds.has(id),
    refresh,
  };
  return <RgpdContext.Provider value={value}>{children}</RgpdContext.Provider>;
}

export function useRgpd() {
  return useContext(RgpdContext);
}
